import { connect, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { State } from "redux/store";
import { useEffect } from "react";

import {
  getImageDataLoader,
  getImageIssues,
  getImageLabels,
  getImageTagLoading,
  getImageTags,
  getSelectedIssues,
  getSelectedLabels,
  getImageUniquenessData,
  getMetadataLinks,
} from "redux/Modals/selectors";
import { getUserTags } from "redux/SingleDataset/selectors";
import { fetchUserTags } from "redux/SingleDataset/actions";

import GeneralBox from "./General";
import VideoBox from "./VideoBox";
import UserTags from "./UserTags";
import IssuesBox from "./Issues";
import VLAccordian from "views/components/Accordian";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import { truncateFilename } from "helpers/utility/utilities";

import styles from "./style.module.scss";
import VLEnrichmentIcon from "assets/icons/VLEnrichmentIcon";
import TagIcon from "assets/icons/tagIcon";
import ShieldIcon from "assets/icons/ShieldIcon";
import ImageMetadataIcon from "assets/icons/ImageMetadataIcon";
import VideoPlayIcon from "assets/icons/VideoPlayIcon";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { USER_TAGS_PARAMETER } from "helpers/constants/miscellaneous";
import { FilterOption } from "types";
import CustomMetadataBox from "./CustomMetadata";

const StatsSection = ({
  img,
  issues,
  labels,
  selectedLabels,
  selectedIssues,
  imageTags,
  uniquenessData,
  isContentLoading,
  isImageTagLoading,
}: any) => {
  const queryParamsFilters = useAllQueryParamsFilters();
  const dispatch = useDispatch();
  const userTagsFromQueryParam = JSON.parse(
    useAllQueryParamsFilters()[USER_TAGS_PARAMETER] || "[]"
  );
  const userTags = useSelector((state: State) =>
    getUserTags(state, userTagsFromQueryParam)
  );
  const metadataLinks = useSelector((state) => getMetadataLinks(state));

  const { datasetId, imageId } = useParams();
  useEffect(() => {
    if (datasetId && imageId) {
      dispatch(fetchUserTags(datasetId, queryParamsFilters) as any);
    }
  }, [datasetId, imageId, queryParamsFilters, dispatch]);

  const imageLabels = labels?.filter(
    (label: any) => label.type === "IMAGE" && label.source === "USER"
  );
  const imageLabelNames = imageLabels.map((label: any) => label.categoryName);
  const objectLables = labels?.filter(
    (label: any) => label.type === "OBJECT" && label.source === "USER"
  );
  const vlImageLabels = labels?.filter(
    (label: any) => label.type === "IMAGE" && label.source === "VL"
  );
  const vlImageLabelNames = vlImageLabels.map(
    (label: any) => label.categoryName
  );
  const vlObjectLables = labels?.filter(
    (label: any) => label.type === "OBJECT" && label.source === "VL"
  );

  const tagsHeaderTitle = `User Tags ${
    !isContentLoading && !isImageTagLoading
      ? `(${imageTags?.length ? formatNumberWithCommas(imageTags.length) : 0})`
      : ""
  }`;

  const updateIssues = (
    issues: { cause: string }[],
    labels: {
      id: string;
      boundingBox: number[];
      name: string;
      color: string;
    }[]
  ) => {
    const labelsForIssues: any = new Map(
      labels.map((label) => [
        label.id,
        {
          color: label.color,
          objectName: label.name.replace(" | ", " "),
          boundingBox: label.boundingBox,
        },
      ])
    );

    // Map through issues and update them with the corresponding label information
    return issues.map((singleIssue) => {
      const labelInfo = labelsForIssues.get(singleIssue.cause) || {
        objectName: null,
        boundingBox: null,
        color: null,
      };

      const { objectName, boundingBox, color } = labelInfo;

      return { ...singleIssue, objectName, boundingBox, color };
    });
  };

  return (
    <div className={styles.statsContainer}>
      <div className={styles.mainBody}>
        {(vlImageLabels?.length > 0 || vlObjectLables?.length > 0) && (
          <VLAccordian
            key="VL Enrichment"
            title="VL Enrichment"
            icon={<VLEnrichmentIcon />}
            className={styles.imagePageAccordian}
          >
            <GeneralBox
              imageLabels={vlImageLabelNames}
              objectLables={vlObjectLables}
              selectedLabels={selectedLabels}
            />
          </VLAccordian>
        )}
        {!!img?.video_uri && (
          <VLAccordian
            key="Video"
            title={
              img.video_name
                ? `Frame from ${truncateFilename(img.video_name, 28)}`
                : "Video"
            }
            icon={<VideoPlayIcon />}
            className={styles.imagePageAccordian}
          >
            <VideoBox
              src={img.video_uri}
              offset={img.frame_timestamp || 0}
              duration={2}
            />
          </VLAccordian>
        )}
        <VLAccordian
          key="General"
          title="General"
          icon={
            <div className={styles.generalIcon}>
              <ImageMetadataIcon />
            </div>
          }
          className={styles.imagePageAccordian}
        >
          <GeneralBox
            img={img}
            imageLabels={imageLabelNames}
            objectLables={objectLables}
            uniquenessData={uniquenessData}
            selectedLabels={selectedLabels}
            metadataLinks={metadataLinks}
          />
        </VLAccordian>
        <VLAccordian
          allowOverflow
          key="User tags"
          title={tagsHeaderTitle}
          icon={<TagIcon />}
          className={styles.imagePageAccordian}
        >
          <UserTags
            imageTags={imageTags}
            userTags={userTags.filter(
              (tag: FilterOption) => tag.text !== "untagged"
            )}
            isContentLoading={isContentLoading}
            isImageTagLoading={isImageTagLoading}
          />
        </VLAccordian>
        {issues && issues.length > 0 && (
          <VLAccordian
            key="Issues"
            title="Issues"
            icon={<ShieldIcon />}
            className={styles.imagePageAccordian}
          >
            <IssuesBox
              issues={updateIssues(issues, selectedLabels)}
              selectedIssues={selectedIssues}
              uniquenessData={uniquenessData}
            />
          </VLAccordian>
        )}

        <CustomMetadataBox />
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    labels: getImageLabels(state),
    issues: getImageIssues(state),
    selectedLabels: getSelectedLabels(state),
    selectedIssues: getSelectedIssues(state),
    imageTags: getImageTags(state),
    isContentLoading: getImageDataLoader(state),
    isImageTagLoading: getImageTagLoading(state),
    uniquenessData: getImageUniquenessData(state),
  };
};

export default connect(mapStateToProps)(StatsSection);
